import React, { useEffect, useState } from "react";
import api from "@/api/client";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { PageHeader, KpiCard, Panel, formatEur } from "@/components/shared/PageUi";
import CrudList from "@/components/shared/CrudList";
import ModuleStaffTab from "@/components/staff/ModuleStaffTab";
import { Users } from "lucide-react";
import { CarFront, Calendar, KeyRound, Wallet } from "lucide-react";

const VEHICLE_STATUS = [
  { value: "available", label: "Vapaa" },
  { value: "rented", label: "Vuokrattu" },
  { value: "service", label: "Huollossa" },
  { value: "out_of_use", label: "Pois käytöstä" },
];
const CATEGORIES = [
  { value: "economy", label: "Economy" },
  { value: "mid", label: "Keskiluokka" },
  { value: "premium", label: "Premium" },
  { value: "suv", label: "SUV" },
  { value: "van", label: "Van" },
];
const TRANSMISSIONS = [{ value: "automatic", label: "Automaatti" }, { value: "manual", label: "Manuaali" }];
const FUELS = [{ value: "petrol", label: "Bensiini" }, { value: "diesel", label: "Diesel" }, { value: "electric", label: "Sähkö" }, { value: "hybrid", label: "Hybridi" }];
const BOOKING_STATUS = [
  { value: "pending", label: "Odottaa" },
  { value: "confirmed", label: "Vahvistettu" },
  { value: "active", label: "Käynnissä" },
  { value: "returned", label: "Palautettu" },
  { value: "cancelled", label: "Peruutettu" },
];
const PAY_STATUS = [
  { value: "pending", label: "Odottaa" },
  { value: "paid", label: "Maksettu" },
  { value: "partial", label: "Osittain" },
  { value: "refunded", label: "Palautettu" },
];

export default function CarRentalDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => { api.get("/car-rental/stats").then((r) => setStats(r.data)).catch(() => {}).finally(() => setLoading(false)); }, []);

  const vehicles = {
    endpoint: "/car-rental/vehicles",
    testidPrefix: "cr-vehicles",
    emptyMessage: "Ei ajoneuvoja",
    defaults: { plate_number: "", category: "economy", daily_rate: 0, status: "available", seats: 5, transmission: "automatic", fuel_type: "petrol", mileage_km: 0 },
    fields: [
      { key: "plate_number", label: "Rekisterinumero", type: "text", required: true },
      { key: "brand", label: "Merkki", type: "text" },
      { key: "model", label: "Malli", type: "text" },
      { key: "year", label: "Vuosi", type: "number", step: "1" },
      { key: "color", label: "Väri", type: "text" },
      { key: "category", label: "Kategoria", type: "select", options: CATEGORIES },
      { key: "seats", label: "Istuimia", type: "number", step: "1" },
      { key: "transmission", label: "Vaihteisto", type: "select", options: TRANSMISSIONS },
      { key: "fuel_type", label: "Polttoaine", type: "select", options: FUELS },
      { key: "daily_rate", label: "Päivähinta €", type: "number" },
      { key: "mileage_km", label: "Mittarilukema", type: "number", step: "1" },
      { key: "status", label: "Tila", type: "select", options: VEHICLE_STATUS },
      { key: "image_url", label: "Kuvan URL", type: "text", span: 2 },
      { key: "notes", label: "Muistiinpanot", type: "textarea", span: 2 },
    ],
    columns: [
      { key: "plate_number", label: "Rek." },
      { key: "brand", label: "Merkki" },
      { key: "model", label: "Malli" },
      { key: "category", label: "Luokka", format: "badge" },
      { key: "daily_rate", label: "€/pv", format: "eur", align: "right" },
      { key: "status", label: "Tila", format: "badge" },
    ],
  };

  const bookings = {
    endpoint: "/car-rental/bookings",
    testidPrefix: "cr-bookings",
    emptyMessage: "Ei varauksia",
    defaults: { customer_name: "", start_date: new Date().toISOString().slice(0, 10), end_date: new Date().toISOString().slice(0, 10), days: 1, daily_rate: 0, status: "pending", payment_status: "pending" },
    fields: [
      { key: "customer_name", label: "Asiakas", type: "text", required: true },
      { key: "customer_phone", label: "Puhelin", type: "text" },
      { key: "customer_email", label: "Sähköposti", type: "text" },
      { key: "customer_id_number", label: "Henkilötunnus", type: "text" },
      { key: "customer_license", label: "Ajokorttinumero", type: "text" },
      { key: "vehicle_plate", label: "Auton rek.nro", type: "text" },
      { key: "vehicle_name", label: "Auton nimi", type: "text" },
      { key: "start_date", label: "Vuokra alkaa", type: "date", required: true },
      { key: "end_date", label: "Vuokra loppuu", type: "date", required: true },
      { key: "days", label: "Päiviä", type: "number", step: "1" },
      { key: "daily_rate", label: "€/päivä", type: "number" },
      { key: "deposit", label: "Vakuus €", type: "number" },
      { key: "pickup_location", label: "Noutopaikka", type: "text" },
      { key: "return_location", label: "Palautuspaikka", type: "text" },
      { key: "mileage_out", label: "Mittarilukema lähtö", type: "number", step: "1" },
      { key: "mileage_in", label: "Mittarilukema palaut.", type: "number", step: "1" },
      { key: "fuel_level_out", label: "Tankki lähtö", type: "text", placeholder: "Täysi / 3/4..." },
      { key: "fuel_level_in", label: "Tankki palaut.", type: "text" },
      { key: "status", label: "Tila", type: "select", options: BOOKING_STATUS },
      { key: "payment_status", label: "Maksun tila", type: "select", options: PAY_STATUS },
      { key: "notes", label: "Muistiinpanot", type: "textarea", span: 2 },
    ],
    columns: [
      { key: "booking_number", label: "Nro" },
      { key: "customer_name", label: "Asiakas" },
      { key: "vehicle_plate", label: "Auto" },
      { key: "start_date", label: "Alkaa" },
      { key: "end_date", label: "Päättyy" },
      { key: "total_price", label: "Hinta", format: "eur", align: "right" },
      { key: "status", label: "Tila", format: "badge" },
    ],
  };

  return (
    <div className="space-y-6" data-testid="car-rental-dashboard">
      <PageHeader kicker="Sovellus" title="Autovuokraus" description="Ajoneuvot ja varaukset." />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard icon={<CarFront className="w-5 h-5" />} label="Vapaat autot" value={stats?.vehicles_available ?? 0} accent="text-emerald-300" loading={loading} testid="cr-kpi-avail" />
        <KpiCard icon={<KeyRound className="w-5 h-5" />} label="Vuokrattu" value={stats?.vehicles_rented ?? 0} accent="text-amber-300" loading={loading} testid="cr-kpi-rented" />
        <KpiCard icon={<Calendar className="w-5 h-5" />} label="Aktiiviset varaukset" value={stats?.active_bookings ?? 0} accent="text-blue-300" loading={loading} testid="cr-kpi-active" />
        <KpiCard icon={<Wallet className="w-5 h-5" />} label="LV yht." value={formatEur(stats?.revenue_total)} accent="text-amber-300" loading={loading} testid="cr-kpi-revenue" />
      </div>
      <Panel title="Hallinta">
        <Tabs defaultValue="bookings" className="w-full">
          <TabsList className="bg-amber-500/10 mb-4">
            <TabsTrigger value="bookings" data-testid="cr-tab-bookings">Varaukset</TabsTrigger>
            <TabsTrigger value="vehicles" data-testid="cr-tab-vehicles">Autot</TabsTrigger>
            <TabsTrigger value="staff" data-testid="cr-tab-staff"><Users className="w-3.5 h-3.5 mr-1.5" />Henkilökunta</TabsTrigger>
          </TabsList>
          <TabsContent value="bookings"><CrudList config={bookings} /></TabsContent>
          <TabsContent value="vehicles"><CrudList config={vehicles} /></TabsContent>
          <TabsContent value="staff"><ModuleStaffTab moduleKey="car_rental" moduleLabel="Autovuokraus" /></TabsContent>
        </Tabs>
      </Panel>
    </div>
  );
}
